import { useState } from "react";
import { Box, Button, Grid, Paper, TextField, Typography } from "@mui/material";
import { DataStructureFormProps, registerForm } from "./FormRegistry";
import { IDataEntity } from "services/backend/DataSchemaService";

export interface WalletData extends IDataEntity {
  currencies: [
    {
      id: "currency_coin";
      amount: number;
    },
    {
      id: "currency_gem";
      amount: number;
    },
  ];
}

const WalletForm = ({ data, onSave, onCancel }: DataStructureFormProps<WalletData>) => {
  const [gems, setGems] = useState<number>(data.currencies.find((c) => c.id === "currency_gem")?.amount || 0);
  const [gold, setGold] = useState<number>(data.currencies.find((c) => c.id === "currency_coin")?.amount || 0);
  const [gemsError, setGemsError] = useState<string>("");
  const [goldError, setGoldError] = useState<string>("");

  const validateNumber = (value: number, fieldName: string): boolean => {
    if (isNaN(value) || value < 0) {
      if (fieldName === "gems") {
        setGemsError("Must be a positive number");
      } else {
        setGoldError("Must be a positive number");
      }
      return false;
    }
    if (fieldName === "gems") {
      setGemsError("");
    } else {
      setGoldError("");
    }
    return true;
  };

  const handleSave = async () => {
    const isGemsValid = validateNumber(gems, "gems");
    const isGoldValid = validateNumber(gold, "gold");

    if (!isGemsValid || !isGoldValid) {
      return;
    }

    const updatedData: WalletData = {
      ...data,
      currencies: [
        {
          id: "currency_coin",
          amount: gold,
        },
        {
          id: "currency_gem",
          amount: gems,
        },
      ],
    };

    const success = await onSave(updatedData);
    if (success) {
      onCancel();
    }
  };

  return (
    <Paper sx={{ width: "100%", p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Edit Wallet Data
      </Typography>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={2}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Wallet ID: {data.id}
          </Typography>
        </Grid>
        <Grid size={2}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Version: {data.version}
          </Typography>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid size={6}>
          <TextField
            fullWidth
            label="Gems"
            type="number"
            value={gems}
            onChange={(e) => setGems(Number(e.target.value))}
            error={!!gemsError}
            helperText={gemsError}
          />
        </Grid>

        <Grid size={6}>
          <TextField
            fullWidth
            label="Gold"
            type="number"
            value={gold}
            onChange={(e) => setGold(Number(e.target.value))}
            error={!!goldError}
            helperText={goldError}
          />
        </Grid>

        <Grid size={12}>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button variant="outlined" onClick={onCancel}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" onClick={handleSave}>
              Save Changes
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

registerForm("Wallet", WalletForm);

export default WalletForm;
